import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Sprout, BookOpen, ShieldCheck, Leaf } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import ClassCard from "@/components/ClassCard";
import TestimonialsSection from "@/components/TestimonialsSection";
import GalleryPreviewSection from "@/components/GalleryPreviewSection";
import SEO from "@/components/SEO";
import { useSiteSection } from "@/lib/useSiteSection";
import { api } from "@/lib/api";
import {
  buildLocalBusiness, buildWebSite, buildBreadcrumb,
  buildFaqPage, buildCourse, buildEvent, buildCourseItemList,
} from "@/lib/schemas";
import { HOME } from "@/constants/testIds";

const HERO_IMG = "/hero-garden.jpg";

const ABOUT_IMG = "/about-heather.jpg";

const CURRICULUM_IMG =
  "https://images.unsplash.com/photo-1551772413-6c1b7dc18548?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxib3RhbmljYWwlMjBoZXJicyUyMGZsYXRsYXl8ZW58MHx8fHwxNzgwNDIzOTEzfDA&ixlib=rb-4.1.0&q=85";

const FAQS = [
  {
    q: "How does ESA / ClassWallet payment work?",
    a: "Graceful Garden Coaching is a Registered ClassWallet Direct Pay Vendor. After registering, you'll receive a fully formatted invoice (PDF) keyed to your ESA contract number with Arizona academic standards alignment. You have 7 days to upload it to ClassWallet to secure your child's spot. Final approval of any given invoice is between you and the State of Arizona. You may also opt out and pay directly by credit card at checkout.",
  },
  {
    q: "What should we wear and bring?",
    a: "Closed-toe shoes are required — we work with soil, garden tools, and livestock-adjacent areas. Layered clothing for changing Arizona weather, a refillable water bottle, a hat or sun protection, and a folding lawn or camping chair if a sit-down portion is part of the class. Every attending adult signs our homestead liability waiver at the welcome table on arrival.",
  },
  {
    q: "Can a parent attend with their student?",
    a: "Yes — chaperoning is always open and free. If a class includes a parent-participation add-on (like a cooking class portion), there will be an optional add-on fee paid by credit card at checkout — clearly noted on the class page.",
  },
  {
    q: "Where do classes take place?",
    a: "Our working homestead in Chino Valley, Arizona (Yavapai County). Designated learning areas are clearly marked. Out of respect for our animals and the land, please remain within those areas throughout the visit.",
  },
  {
    q: "What ages are classes for?",
    a: "Most group classes are designed for K–8 homestead-curious learners. From time to time — and whenever there's enough interest — Heather also offers high-school-age sessions. 1-on-1 sessions can be tailored from age 5 through adults, so let us know the student's age and learning interests when you book.",
  },
  {
    q: "What if my student has allergies or dietary needs?",
    a: "Please share any allergies or sensitivities in the registration notes (or by email after booking) so Heather can adapt cooking/fermentation portions or arrange a parallel activity. Common allergens like wheat, dairy, eggs, and tree nuts may appear in food-related classes.",
  },
  {
    q: "What's your cancellation and refund policy?",
    a: "Full refund for cancellations more than 14 days before class · class credit 14–5 days out · no refund inside 5 days. Full policy on our Terms page.",
  },
  {
    q: "What if weather is bad?",
    a: "Arizona weather is mostly forgiving, but we'll reschedule or shift indoors for monsoon storms, extreme heat advisories, or wildfire smoke. You'll get an email the morning of class if anything changes.",
  },
];

const STEPS = [
  { icon: Sprout, label: "Pick Your Class", body: "Browse upcoming homestead workshops and field trips below." },
  { icon: BookOpen, label: "Register the Student", body: "Enter the student name, parent contact, and ESA contract number." },
  { icon: ShieldCheck, label: "Sign the Waiver", body: "Read our homestead waiver and digitally sign with your full name." },
  { icon: Leaf, label: "Get Your Invoice", body: "Download your ESA-ready invoice. Upload to ClassWallet within 7 days." },
];

export default function LandingPage() {
  const { data: classes = [], isLoading } = useQuery({
    queryKey: ["classes"],
    queryFn: async () => (await api.get("/classes")).data,
  });

  // Editable site content (with safe defaults so the page always renders)
  const hero = useSiteSection("home_hero", {
    eyebrow: "A working homestead classroom · Chino Valley, AZ",
    headline_line1: "Learning that grows", headline_line2: "in good soil.",
    subhead: "Hands-on homestead education for Arizona homeschool families.",
    badge_text: "Registered ClassWallet Direct Pay Vendor",
    primary_cta_label: "See upcoming classes", primary_cta_url: "#classes",
    secondary_cta_label: "Meet Heather", secondary_cta_url: "#about",
    script_tag: "growing things on purpose",
    scroll_cue: "↓ Scroll for upcoming classes",
  });
  const about = useSiteSection("home_about", {});
  const learn = useSiteSection("home_learn", {});
  const cls_section = useSiteSection("home_classes", {});
  const oneOnOne = useSiteSection("home_1on1", {});
  const faq = useSiteSection("home_faq", { items: FAQS });

  // JSON-LD schemas — LocalBusiness + WebSite + FAQPage + BreadcrumbList + per-class Course & Event
  const faqItems = faq.items || FAQS;
  const schemas = [
    buildLocalBusiness(),
    buildWebSite(),
    buildBreadcrumb([{ name: "Home", url: "/" }]),
    buildFaqPage(faqItems),
    ...(classes.length > 0 ? [buildCourseItemList(classes)] : []),
    ...classes.map(buildCourse),
    ...classes.map(buildEvent),
  ];

  // Hidden API ping (preserves home test-id contract expected by template)
  useEffect(() => {
    api.get("/").catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Hands-On Homestead Classes for Arizona Homeschool Families"
        description="Registered ClassWallet Direct Pay Vendor in Chino Valley, AZ. Homestead field trips, gardening, cooking, and 1-on-1 education for K-8 students across Yavapai County."
        keywords="Arizona ESA classes, ClassWallet vendor, homestead education, Chino Valley homeschool, Yavapai County field trips, homestead workshops"
        jsonLd={schemas}
      />
      <NavBar />

      {/* HERO — asymmetric split, organic */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up d1">
            <p className="uppercase tracking-[0.22em] text-xs sm:text-sm text-gg-brown font-semibold mb-5">
              {hero.eyebrow}
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--gg-text)]">
              {hero.headline_line1}
              <span className="block italic text-gg-green">{hero.headline_line2}</span>
            </h1>
            <p className="mt-7 text-base sm:text-lg text-gg-muted max-w-xl leading-relaxed">
              {hero.subhead}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gg-green/40 bg-gg-green/8 text-xs text-gg-green font-semibold">
              <Leaf size={13} /> {hero.badge_text}
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a href={hero.primary_cta_url} className="btn-pill btn-primary" data-testid={HOME.heroCta}>
                {hero.primary_cta_label} <ArrowRight size={16} />
              </a>
              {hero.secondary_cta_label && (
                <a href={hero.secondary_cta_url} className="btn-pill btn-outline">
                  {hero.secondary_cta_label}
                </a>
              )}
            </div>
            <p
              className="mt-10 text-xs uppercase tracking-[0.22em] text-gg-muted"
              data-testid={HOME.heroScrollCue}
            >
              {hero.scroll_cue}
            </p>
          </div>

          <div className="lg:col-span-5 fade-up d2">
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2.4rem] bg-gg-green/8 -z-10 rotate-1" />
              <img
                src={HERO_IMG}
                alt="Hands planting in dark, healthy soil"
                className="rounded-[2rem] w-full object-cover aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(45,53,41,0.35)]"
              />
              <div className="absolute -bottom-6 -left-6 bg-gg-surface border border-gg-border rounded-2xl px-4 py-3 shadow-lg hidden sm:block">
                <p className="font-script text-2xl text-gg-green leading-none">
                  {hero.script_tag}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* organic divider */}
        <div className="texture-grain absolute inset-0 -z-10 opacity-50" />
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-gg-surface border-y border-gg-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <p className="uppercase tracking-[0.22em] text-xs text-gg-brown font-semibold mb-4">
            How registration works
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[var(--gg-text)] max-w-3xl">
            Four calm steps from interest to invoice.
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map(({ icon: Icon, label, body }, i) => (
              <div
                key={label}
                className={`rounded-2xl border border-gg-border p-6 fade-up d${i + 1}`}
              >
                <div className="w-11 h-11 rounded-xl bg-gg-green/10 flex items-center justify-center text-gg-green mb-4">
                  <Icon size={22} />
                </div>
                <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
                  Step {i + 1}
                </p>
                <h3 className="font-heading text-xl text-[var(--gg-text)] mt-1">
                  {label}
                </h3>
                <p className="text-sm text-gg-muted mt-2 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UPCOMING CLASSES */}
      <section id="classes" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <p className="uppercase tracking-[0.22em] text-xs text-gg-brown font-semibold mb-3">
              {cls_section.eyebrow || "Upcoming"}
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)]">
              {cls_section.heading || "Field trips & workshops"}
            </h2>
          </div>
          <p className="text-sm text-gg-muted max-w-md">
            {cls_section.intro || "All listed prices are billed through a ClassWallet-ready invoice."}
          </p>
        </div>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 rounded-2xl border border-gg-border bg-gg-surface animate-pulse" />
            ))}
          </div>
        ) : classes.length === 0 ? (
          <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 sm:p-16 text-center">
            <p className="font-heading text-2xl sm:text-3xl text-[var(--gg-text)]">
              {cls_section.empty_state_heading || "New classes are being prepared."}
            </p>
            <p className="text-gg-muted mt-3 max-w-lg mx-auto">
              {cls_section.empty_state_body || "Check back soon."}
            </p>
            {cls_section.empty_state_cta_label && cls_section.empty_state_cta_url && (
              <Link to={cls_section.empty_state_cta_url} className="btn-pill btn-primary inline-flex mt-6">
                {cls_section.empty_state_cta_label} <ArrowRight size={16} />
              </Link>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {classes.map((c) => (
              <ClassCard key={c.id} cls={c} />
            ))}
          </div>
        )}
      </section>

      {/* ABOUT HEATHER */}
      <section id="about" className="bg-gg-surface border-y border-gg-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img
              src={ABOUT_IMG}
              alt="Heather Mich, founder of Graceful Garden Coaching, at her homestead"
              className="rounded-[2rem] w-full object-cover aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(45,53,41,0.32)]"
            />
          </div>
          <div className="lg:col-span-7">
            <p className="uppercase tracking-[0.22em] text-xs text-gg-brown font-semibold mb-4">
              {about.eyebrow || "About Heather"}
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] leading-tight">
              {about.heading || "A homesteader teaching kids what the seed already knows."}
            </h2>
            <div className="mt-6 space-y-4 text-[var(--gg-text)]/90 leading-relaxed">
              {(about.body || "").split(/\n\s*\n/).filter(Boolean).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
            <div className="mt-8 grid sm:grid-cols-3 gap-5">
              <div>
                <p className="font-heading text-4xl text-gg-green leading-none">{about.stat1_value || "5+"}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-gg-brown mt-2 font-semibold">
                  {about.stat1_label || "Years coaching"}
                </p>
              </div>
              <div>
                <p className="font-heading text-4xl text-gg-green leading-none">{about.stat2_value || "28+"}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-gg-brown mt-2 font-semibold">
                  {about.stat2_label || "Families served"}
                </p>
              </div>
              <div>
                <p className="font-heading text-4xl text-gg-green leading-none">{about.stat3_value || "100%"}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-gg-brown mt-2 font-semibold">
                  {about.stat3_label || "Hands-on outdoors"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <p className="uppercase tracking-[0.22em] text-xs text-gg-brown font-semibold mb-4">
            {learn.eyebrow || "What you'll learn"}
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] leading-tight">
            {learn.heading || "From garden to table — and every craft in between."}
          </h2>
          <p className="mt-5 text-gg-muted leading-relaxed max-w-xl">
            {learn.intro || "Each class spotlights a different homestead skill."}
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[var(--gg-text)]/90">
            {(learn.bullets || []).map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-2 w-2 h-2 rounded-full bg-gg-green shrink-0" />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          {learn.age_note && (
            <p className="mt-6 rounded-xl border border-gg-border bg-gg-bg px-4 py-3 text-sm text-gg-muted italic max-w-xl">
              {learn.age_note}
            </p>
          )}
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <img
            src={CURRICULUM_IMG}
            alt="Botanical flatlay of fresh herbs and leafy greens"
            className="rounded-[2rem] w-full object-cover aspect-[5/4] shadow-[0_30px_80px_-30px_rgba(45,53,41,0.32)]"
          />
        </div>
      </section>

      {/* 1-on-1 CTA */}
      <section className="bg-gg-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="rounded-[2rem] border border-gg-border bg-gg-surface p-8 sm:p-14 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="uppercase tracking-[0.22em] text-xs text-gg-brown font-semibold mb-4">
                One-on-One
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] leading-tight">
                1-on-1, consultations, &amp; private groups.
              </h2>
              <p className="mt-5 text-gg-muted leading-relaxed max-w-xl">
                Private homestead coaching, on-location property consultations, and make-and-take group classes — open to students and adults, at the homestead or at your place. Each 1-on-1 includes a take-home curriculum packet so the learning continues at home.
              </p>
              <Link to="/services" className="btn-pill btn-primary mt-7 inline-flex" data-testid="home-book-1on1-cta">
                Browse Services <ArrowRight size={16} />
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-gg-bg border border-gg-border p-6 space-y-4 text-sm">
                <Row icon={Sprout} title="Pick your subject" body="Gardening, baking, fiber arts, animal care — tell Heather and she'll build the hour around it." />
                <Row icon={BookOpen} title="Take-home curriculum" body="Every session includes a printed packet so your student keeps learning at home." />
                <Row icon={ShieldCheck} title="Calendar-confirmed" body="Heather picks a time on her real calendar — no double-booking." />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo gallery preview (renders nothing when no photos uploaded yet) */}
      <GalleryPreviewSection />

      {/* Testimonials (renders nothing when admin hasn't added any) */}
      <TestimonialsSection />

      {/* FAQ */}
      <section id="faq" className="bg-gg-surface border-y border-gg-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <p className="uppercase tracking-[0.22em] text-xs text-gg-brown font-semibold mb-4">
            {faq.eyebrow || "ESA & FAQ"}
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] mb-10">
            {faq.heading || "Questions parents ask first."}
          </h2>
          <div className="divide-y divide-gg-border">
            {(faq.items || []).map((f, i) => (
              <FaqRow key={i} faq={f} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

function Row({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-gg-green/10 text-gg-green flex items-center justify-center shrink-0">
        <Icon size={16} />
      </div>
      <div>
        <p className="font-semibold text-[var(--gg-text)]">{title}</p>
        <p className="text-gg-muted">{body}</p>
      </div>
    </div>
  );
}

function FaqRow({ faq, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="py-5" data-testid={HOME.faqItem(index)}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-6 text-left"
      >
        <span className="font-heading text-xl sm:text-2xl text-[var(--gg-text)] leading-snug">
          {faq.q}
        </span>
        <span className={`text-gg-green text-2xl transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && (
        <p className="mt-3 text-gg-muted leading-relaxed max-w-2xl">{faq.a}</p>
      )}
    </div>
  );
}
